import React, { useState } from 'react';
import { ComponentType } from '../types';
import { NODE_CONFIG } from '../constants';
import { ChevronLeft, ChevronRight, Menu } from 'lucide-react';
import Logo from './Logo';

interface SidebarProps {
  onComponentClick?: (type: ComponentType) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onComponentClick }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const onDragStart = (event: React.DragEvent, nodeType: ComponentType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleComponentClick = (type: ComponentType, event: React.MouseEvent) => {
    // Only trigger on direct click, not on drag
    if (event.detail > 0 && onComponentClick) {
      onComponentClick(type);
    }
  };

  return (
    <div 
      className={`${isCollapsed ? 'w-16' : 'w-64'} bg-white border-r border-black/10 flex flex-col h-full z-10 relative shrink-0 transition-all duration-300 ease-in-out will-change-width`}
    >
      <button
         onClick={() => setIsCollapsed(!isCollapsed)}
         className="absolute -right-3 top-5 bg-white border border-black/10 rounded-full p-1 text-black hover:bg-gray-50 z-50 shadow-sm transition-transform hover:scale-110"
         title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
      >
        {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>

      <div className={`p-4 border-b border-black/10 h-[69px] flex items-center transition-opacity duration-300 ${isCollapsed ? 'justify-center opacity-100' : 'opacity-100'}`}>
        {isCollapsed ? (
           <Logo 
             size={32}
             className="flex-shrink-0"
             title="ArchWeaver"
           />
        ) : (
          <div className="flex items-center gap-3">
            <Logo 
              size={40}
              className="flex-shrink-0"
            />
            <div>
              <h2 className="text-lg font-bold text-black tracking-tight">
                ArchWeaver
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">Drag to canvas</p>
            </div>
          </div>
        )}
      </div>
      
      <div className={`flex-1 overflow-y-auto p-2 space-y-2 ${isCollapsed ? 'overflow-x-hidden' : ''}`}>
        {([
          ComponentType.API_GATEWAY,
          ComponentType.WEB_SERVER,
          ComponentType.MICROSERVICE,
          ComponentType.LOAD_BALANCER,
          ComponentType.FUNCTION,
          ComponentType.WORKER,
          ComponentType.DATABASE,
          ComponentType.CACHE,
          ComponentType.SEARCH_ENGINE,
          ComponentType.MESSAGE_BROKER,
          ComponentType.STORAGE,
          ComponentType.CDN,
          ComponentType.FIREWALL,
          ComponentType.KUBERNETES,
          ComponentType.AUTH_SERVICE,
          ComponentType.NOTIFICATION,
          ComponentType.GROUP
        ] as ComponentType[]).map((type) => {
          const config = NODE_CONFIG[type];
          return (
            <button
              key={type}
              data-testid={`component-${type}`}
              id={`component-button-${type}`}
              className={`
                flex items-center rounded-none border border-transparent w-full
                cursor-grab active:cursor-grabbing transition-all group
                ${isCollapsed 
                  ? 'justify-center p-2 hover:bg-gray-100' 
                  : 'gap-3 p-3 hover:border-black/20 hover:bg-gray-50'
                }
              `}
              onDragStart={(event) => onDragStart(event, type)}
              onClick={(event) => handleComponentClick(type, event)}
              draggable
              tabIndex={0}
              role="button"
              title={isCollapsed ? type : `${type} - Click to add or drag to canvas`}
              aria-label={`Add ${type} component`}
            >
              <div 
                className={`
                  rounded-none text-black transition-all border border-transparent
                  ${isCollapsed 
                    ? 'p-2 bg-gray-50 group-hover:bg-white group-hover:shadow-sm group-hover:border-black/10' 
                    : 'p-1.5 bg-gray-100 group-hover:bg-white group-hover:shadow-sm group-hover:border-black/10'
                  }
                `}
              >
                {config.icon}
              </div>
              
              {!isCollapsed && (
                <div className={`min-w-0 text-left transition-opacity duration-300 ${isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
                  <div className="text-sm font-medium text-black group-hover:text-black truncate">{type}</div>
                  <div className="text-[10px] text-gray-400 group-hover:text-gray-600 truncate">{config.description}</div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className={`p-4 border-t border-black/10 bg-gray-50/50 ${isCollapsed ? 'flex justify-center' : ''}`}>
        {isCollapsed ? (
          <div className="text-[10px] font-bold text-gray-400">v1.0</div>
        ) : (
          <div className="text-xs text-gray-400 text-center">
             v1.0 • Minimal Mode
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;